import { describeEmailBounceCode } from '@orn/global/delivery/emailBounceCodes.js';
import { describeSmsError } from '@orn/global/delivery/smsErrorCodes.js';
import formatElapsed from '@orn/shared-web/utils/datetime/formatElapsed';
import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';

import {
  AdminChannelHealthState,
  AdminChannelOptOutSource,
  AdminOutreachChannel,
  AdminSmsFaultClass,
} from '../../__generated__/globalTypes';
import { formatDurationMs } from '../Assistant/transcript/transcriptModel';

import type { ChannelHealthFieldsFragment } from './__generated__/channelHealthFields.gql';

export type ChannelHealthTone = 'success' | 'warning' | 'error' | 'neutral';

/** Each channel as a row names it. */
export const CHANNEL_NAMES: Record<AdminOutreachChannel, string> = {
  [AdminOutreachChannel.Sms]: 'Text',
  [AdminOutreachChannel.Email]: 'Email',
};

/**
 * Only `HEALTHY` and `FAILING` leave the channel open. `FAILING` is amber
 * rather than green: nothing has been delivered since its last send failed.
 */
const STATE_BADGES: Record<AdminChannelHealthState, { label: string; tone: ChannelHealthTone }> = {
  [AdminChannelHealthState.Healthy]: { label: 'healthy', tone: 'success' },
  [AdminChannelHealthState.Failing]: { label: 'failing', tone: 'warning' },
  [AdminChannelHealthState.Paused]: { label: 'paused', tone: 'warning' },
  [AdminChannelHealthState.Undeliverable]: { label: 'undeliverable', tone: 'error' },
  [AdminChannelHealthState.OptedOut]: { label: 'opted out', tone: 'neutral' },
  [AdminChannelHealthState.Unverified]: { label: 'unverified', tone: 'neutral' },
};

/** The state badge's word and tone. */
export function channelHealthBadge(state: AdminChannelHealthState): { label: string; tone: ChannelHealthTone } {
  return STATE_BADGES[state];
}

const SMS_FAULT_CLASS_LABELS: Record<AdminSmsFaultClass, string> = {
  [AdminSmsFaultClass.Recipient]: 'their number',
  [AdminSmsFaultClass.Unsupported]: 'their handset',
  [AdminSmsFaultClass.Transient]: 'transient',
  [AdminSmsFaultClass.Sender]: 'our side',
};

const OPT_OUT_SOURCE_LABELS: Record<AdminChannelOptOutSource, string> = {
  [AdminChannelOptOutSource.Member]: 'by the member',
  [AdminChannelOptOutSource.SpamReport]: 'spam report',
  [AdminChannelOptOutSource.CarrierStop]: 'carrier STOP',
};

/** Whose fault a text's failure code points to, in words; null is a failure reported without a code. */
export function smsFaultClassLabel(faultClass: AdminSmsFaultClass | null): string {
  return faultClass === null ? 'no code' : SMS_FAULT_CLASS_LABELS[faultClass];
}

/** An opt-out source in words. */
export function optOutSourceLabel(source: AdminChannelOptOutSource): string {
  return OPT_OUT_SOURCE_LABELS[source];
}

/** Whether a source can opt a member out of the channel at all: STOP is a text's, a spam report an address's. */
export function optOutSourceApplies(channel: AdminOutreachChannel, source: AdminChannelOptOutSource): boolean {
  switch (source) {
    case AdminChannelOptOutSource.CarrierStop:
      return channel === AdminOutreachChannel.Sms;
    case AdminChannelOptOutSource.SpamReport:
      return channel === AdminOutreachChannel.Email;
    case AdminChannelOptOutSource.Member:
    default:
      return true;
  }
}

/** What a provider code means, or null when the code table does not name it. */
export function deliveryCodeMeaning(channel: AdminOutreachChannel, code: string): string | null {
  return channel === AdminOutreachChannel.Sms ? describeSmsError(code) : describeEmailBounceCode(code);
}

/** A provider code with what it means, or the bare code when the table does not name it. */
function formatDeliveryCode(channel: AdminOutreachChannel, code: string): string {
  const label = deliveryCodeMeaning(channel, code);
  return label === null ? code : `${label} (${code})`;
}

/** `verb` with how long ago (or ahead) `instant` is, or `verb` alone when there is no instant. */
function stamped(verb: string, instant: string | null | undefined, now: Date): string {
  const distance = instant ? formatElapsed(instant, now) : null;
  return distance === null ? verb : `${verb} ${distance}`;
}

function joined(parts: readonly (string | null)[]): string | null {
  const kept = parts.filter((part): part is string => part !== null);
  return kept.length === 0 ? null : kept.join(' · ');
}

/**
 * Every assistant delivery also moves a text's reach, so reach is news only
 * when a reply or another of our numbers moved it later.
 */
function reachedSinceDelivery(health: ChannelHealthFieldsFragment): boolean {
  if (!health.lastReachedAt) {
    return false;
  }
  return !health.lastDeliveredAt || Date.parse(health.lastReachedAt) > Date.parse(health.lastDeliveredAt);
}

/**
 * The line beside the badge: when, which code, and whose fault. Null for an
 * unverified channel, where the badge says it all.
 */
export function channelHealthDetail(health: ChannelHealthFieldsFragment, now: Date = new Date()): string | null {
  const isText = health.channel === AdminOutreachChannel.Sms;
  const failure = [
    stamped(isText ? 'failed' : 'bounced', health.since, now),
    health.code ? formatDeliveryCode(health.channel, health.code) : null,
    health.faultClass ? SMS_FAULT_CLASS_LABELS[health.faultClass] : null,
  ];

  switch (health.state) {
    case AdminChannelHealthState.Healthy:
      return (
        joined([
          health.lastDeliveredAt
            ? stamped(isText ? 'assistant text delivered' : 'delivered', health.lastDeliveredAt, now)
            : null,
          reachedSinceDelivery(health) ? stamped('any delivery or reply', health.lastReachedAt, now) : null,
        ]) ?? 'no deliveries on record'
      );
    case AdminChannelHealthState.Failing:
    case AdminChannelHealthState.Undeliverable:
      return joined(failure);
    case AdminChannelHealthState.Paused:
      return joined([...failure, health.reopensAt ? stamped('reopens', health.reopensAt, now) : null]);
    case AdminChannelHealthState.OptedOut:
      return health.optOutSource ? stamped(OPT_OUT_SOURCE_LABELS[health.optOutSource], health.since, now) : null;
    case AdminChannelHealthState.Unverified:
    default:
      return null;
  }
}

// SES keeps rejecting an address on its suppression list whatever we later learn about it.
const SES_SUPPRESSION_CODES: ReadonlySet<string> = new Set(['Suppressed', 'OnAccountSuppressionList']);

/** What the state means for the channel, and what lifts it. */
function explainState(health: ChannelHealthFieldsFragment): string {
  const isText = health.channel === AdminOutreachChannel.Sms;
  switch (health.state) {
    case AdminChannelHealthState.Healthy:
      return 'No unresolved delivery failure.';
    case AdminChannelHealthState.Failing:
      return isText
        ? 'Open again, but the last assistant text failed and none has been delivered since.'
        : 'Open again, but the last email bounced and none has been delivered since.';
    case AdminChannelHealthState.Paused:
      return isText
        ? 'A failed assistant text holds outreach here for a day, or until an assistant text is delivered. Replies and our other numbers’ deliveries do not lift it.'
        : 'A bounce holds outreach email for a day, or until mail is delivered or the address is re-verified.';
    case AdminChannelHealthState.Undeliverable:
      if (isText) {
        return 'Twilio reported the number cannot take texts. Lifts at any delivery or reply, or when the member re-verifies it.';
      }
      return health.code && SES_SUPPRESSION_CODES.has(health.code)
        ? 'SES suppresses this address and keeps rejecting it until it is removed from the suppression list.'
        : 'SES reported the address cannot take mail. Lifts at a later delivery or when the member re-verifies it.';
    case AdminChannelHealthState.OptedOut:
      if (health.optOutSource === AdminChannelOptOutSource.CarrierStop) {
        return 'The number texted STOP. Every text is blocked, replies included, until the member texts START.';
      }
      if (health.optOutSource === AdminChannelOptOutSource.SpamReport) {
        return 'A spam report opted the member out of outreach email. Replies still go out.';
      }
      return `The member left proactive ${isText ? 'texts' : 'email'}. Replies still go out.`;
    case AdminChannelHealthState.Unverified:
    default:
      return isText ? 'No verified number, so nothing is texted.' : 'No verified address, so nothing is emailed.';
  }
}

/** The badge's tooltip: the channel and state, what that means, then each stamp behind it in Pacific time. */
export function channelHealthTooltip(health: ChannelHealthFieldsFragment): { title: string; lines: string[] } {
  const isText = health.channel === AdminOutreachChannel.Sms;
  const stamps: Array<[string, string | null | undefined]> = [
    [isText ? 'Last assistant text delivered' : 'Last delivered', health.lastDeliveredAt],
    ['Last delivery or reply on any number', health.lastReachedAt],
    [isText ? 'Last assistant text failed' : 'Last bounced', health.lastFailedAt],
    ['Reopens', health.reopensAt],
  ];
  return {
    title: `${CHANNEL_NAMES[health.channel]} ${STATE_BADGES[health.state].label}`,
    lines: [
      explainState(health),
      ...stamps.flatMap(([label, instant]) => (instant ? [`${label} ${formatPacificDateTime(instant)}`] : [])),
    ],
  };
}

/** A lowercase label as a row heading reads it: first letter up, the rest untouched. */
export function sentenceCase(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** The windows the fleet report offers, in days. */
export const CHANNEL_HEALTH_WINDOWS: ReadonlyArray<{ days: number; label: string }> = [
  { days: 1, label: '24h' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
];

export const DEFAULT_CHANNEL_HEALTH_WINDOW_DAYS = 7;

/**
 * Below this many openings a rate or a percentile is noise — one failed text of
 * two reads as a 50% failure rate — so the tool shows the count alone.
 */
export const MIN_SAMPLE_SIZE = 20;

/**
 * `count` as a whole-number share of `total`, or null while `total` is below
 * {@link MIN_SAMPLE_SIZE}. A share that is neither none nor all never rounds to
 * 0% or 100%.
 */
export function formatShare(count: number, total: number): string | null {
  if (total < MIN_SAMPLE_SIZE) {
    return null;
  }
  const percent = Math.round((count / total) * 100);
  if (percent === 0 && count > 0) {
    return '<1%';
  }
  if (percent === 100 && count < total) {
    return '>99%';
  }
  return `${percent}%`;
}

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

/** A latency short enough for a numeric column: milliseconds and seconds as a transcript shows them, then minutes, then hours. */
export function formatLatencyMs(ms: number): string {
  if (ms < MS_PER_MINUTE) {
    return formatDurationMs(ms);
  }
  if (ms < MS_PER_HOUR) {
    return `${(ms / MS_PER_MINUTE).toFixed(1)}m`;
  }
  return `${(ms / MS_PER_HOUR).toFixed(1)}h`;
}

interface IOutreachLatency {
  measured: number;
  p50Ms?: number | null;
  p90Ms?: number | null;
}

/** A latency's p50 and p90 as `p50 / p90`, or null while fewer than {@link MIN_SAMPLE_SIZE} openings were measured. */
export function formatLatency({ measured, p50Ms, p90Ms }: IOutreachLatency): string | null {
  if (measured < MIN_SAMPLE_SIZE || p50Ms === null || p50Ms === undefined || p90Ms === null || p90Ms === undefined) {
    return null;
  }
  return `${formatLatencyMs(p50Ms)} / ${formatLatencyMs(p90Ms)}`;
}

/** Our side first: a rise there is ours to fix, where the rest point at the member's end or a passing fault. */
const SMS_FAULT_CLASS_ORDER: readonly AdminSmsFaultClass[] = [
  AdminSmsFaultClass.Sender,
  AdminSmsFaultClass.Transient,
  AdminSmsFaultClass.Recipient,
  AdminSmsFaultClass.Unsupported,
];

/**
 * A text's failed openings summed by whose fault their code points to, our side
 * first and every class present. Failures reported without a code close the
 * list, and only when there are some.
 */
export function failuresByFaultClass(
  failureCodes: ReadonlyArray<{ faultClass?: AdminSmsFaultClass | null; openings: number }>
): Array<{ faultClass: AdminSmsFaultClass | null; openings: number }> {
  const openingsOf = (faultClass: AdminSmsFaultClass | null) =>
    failureCodes
      .filter((entry) => (entry.faultClass ?? null) === faultClass)
      .reduce((total, entry) => total + entry.openings, 0);
  const uncoded = openingsOf(null);
  return [
    ...SMS_FAULT_CLASS_ORDER.map((faultClass) => ({ faultClass, openings: openingsOf(faultClass) })),
    ...(uncoded > 0 ? [{ faultClass: null, openings: uncoded }] : []),
  ];
}

/** How many members one channel's summary counts in `state`. */
export function membersInState(
  states: ReadonlyArray<{ state: AdminChannelHealthState; members: number }>,
  state: AdminChannelHealthState
): number {
  return states.find((entry) => entry.state === state)?.members ?? 0;
}

/** How many members one channel's summary counts as opted out by `source`. */
export function membersOptedOutBy(
  sources: ReadonlyArray<{ source: AdminChannelOptOutSource; members: number }>,
  source: AdminChannelOptOutSource
): number {
  return sources.find((entry) => entry.source === source)?.members ?? 0;
}

/** The states the attention list carries, in its own order; an opted-out entry there is always a spam report. */
export const ATTENTION_STATES: readonly AdminChannelHealthState[] = [
  AdminChannelHealthState.Undeliverable,
  AdminChannelHealthState.Paused,
  AdminChannelHealthState.Failing,
  AdminChannelHealthState.OptedOut,
];

/** An attention state as its filter names it. */
export function attentionStateLabel(state: AdminChannelHealthState): string {
  return state === AdminChannelHealthState.OptedOut ? 'spam report' : STATE_BADGES[state].label;
}

export interface IAttentionFilter {
  /** Null keeps both channels. */
  channel: AdminOutreachChannel | null;
  /** Null keeps every state. */
  state: AdminChannelHealthState | null;
}

/** The attention entries the filter keeps, in the report's own order. */
export function filterAttention<
  T extends { health: { channel: AdminOutreachChannel; state: AdminChannelHealthState } },
>(items: readonly T[], filter: IAttentionFilter): T[] {
  return items.filter(
    ({ health }) =>
      (filter.channel === null || health.channel === filter.channel) &&
      (filter.state === null || health.state === filter.state)
  );
}
